<template>
  <v-layout column>
    <input
      type="file"
      style="display: none"
      ref="selectedDocuments"
      multiple
      @change="onDocumentsPicked"
    />

    <v-layout column align-center v-if="checklistLoading">
      <v-progress-circular
        indeterminate
        size="40"
        class="ma-5"
        color="primary"
      ></v-progress-circular>
    </v-layout>

    <v-layout column v-else class="mb-2 subheading mt-2">
      <span
        v-if="isEmpty(allDocumentsNeeded) && !hideList"
        class="darkgrey--text body-2 text-xs-left"
        >Documents were not requested for the questionnaire.</span
      >

      <v-layout
        v-if="!hideList"
        v-for="question in allDocumentsNeeded"
        :key="question.id"
        row
        justify-center
        align-start
        class="mb-1 document-item"
        :class="
          isEmpty(question.answer) && showUnanswered && !question.optional
            ? 'error--text outlined-red my-2 pa-2'
            : ''
        "
      >
        <v-flex xs1 sm-7 style="flex: 0" class="mr-2">
          <v-icon
            @click="$emit('toggle-completed', question)"
            style="cursor: pointer"
            v-if="
              !isEmpty(question.answer) &&
                question.answer === 'Marked Completed'
            "
            color="green"
            >check_box</v-icon
          >
          <v-icon
            v-else-if="
              !isEmpty(question.answer) &&
                question.answer !== 'Upload Later' &&
                question.answer !== 'Answer Later' &&
                question.answer !== 'Not Applicable'
            "
            color="green"
            >check_box</v-icon
          >
          <v-icon
            v-else
            color="#666"
            @click="$emit('toggle-completed', question)"
            style="cursor: pointer"
            >check_box_outline_blank</v-icon
          >
        </v-flex>

        <v-flex
          class="text-xs-left"
          :class="$vuetify.breakpoint.xsOnly ? 'caption' : 'body-1'"
        >
          <v-layout align-center class="">
            <span
              :style="
                question.answer === 'Not Applicable'
                  ? 'text-decoration: line-through'
                  : ''
              "
            >
              <span class="document-item-text">{{
                !isEmpty(question.options)
                  ? question.options.join(', ')
                  : question.text
              }}</span>
            </span>

            <template>
              <v-tooltip bottom v-if="question.answer === 'Not Applicable'">
                <v-chip slot="activator" small class="my-0">N/A</v-chip>
                <span>Client marked Not Applicable</span>
              </v-tooltip>
              <v-tooltip
                bottom
                v-else-if="question.answer === 'Provided in Questionnaire'"
              >
                <v-chip slot="activator" small class="my-0"
                  >Marked as Uploaded</v-chip
                >
                <span
                  >Client marked provided (check the documents on the
                  right)</span
                >
              </v-tooltip>
              <v-tooltip bottom v-else-if="question.answer === 'Upload Later'">
                <v-chip
                  slot="activator"
                  small
                  class="my-0"
                  v-if="question.answer_by"
                  >{{ moment(question.answer_by).format('ll') }}</v-chip
                >
                <v-chip slot="activator" small class="my-0" v-else
                  >Upload Later</v-chip
                >
                <span
                  >Client marked upload later{{
                    uploadLaterDate(question)
                  }}</span
                >
              </v-tooltip>
              <v-tooltip
                bottom
                v-else-if="question.answer === 'Provided Elsewhere'"
              >
                <v-chip
                  slot="activator"
                  small
                  class="clickable-chip my-0"
                  small
                  @click="$emit('open-comment-dialog', question)"
                  >Elsewhere</v-chip
                >
                <span
                  >Client marked provided elsewhere (click to check note)</span
                >
              </v-tooltip>
              <v-tooltip
                bottom
                v-else-if="question.answer === 'Marked Completed'"
              >
                <v-chip slot="activator" small class="my-0"
                  >Marked Completed</v-chip
                >
                <span>You marked as completed</span>
              </v-tooltip>
              <v-tooltip bottom v-else-if="!isEmpty(question.answer)">
                <v-chip slot="activator" small class="my-0">Uploaded</v-chip>
                <span>Uploaded {{ listDocumentsWording(question) }}</span>
              </v-tooltip>
            </template>

            <v-tooltip bottom v-if="recognizedDocument(question)">
              <v-icon
                slot="activator"
                @click="openRecognizedDocument(question)"
                style="cursor: pointer"
                color="primary"
                class="ml-1"
                size="20"
                >task</v-icon
              >
              <span>View Recognized Document</span>
            </v-tooltip>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-layout>
  </v-layout>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import { mapGetters } from 'vuex';
import get from 'lodash/get';
import API from '@/api';
import moment from 'moment';
import { predefinedQuestionAnswers } from '@/constants/variables';
import FirmPreferenceMixins from '../mixins/FirmPreferenceMixins';

export default {
  props: {
    checklistLoading: {
      type: Boolean,
    },

    allDocuments: {
      type: Array,
      required: false,
    },

    currentQuestionnaire: {
      type: Object,
      default: null,
    },

    remainingItems: {
      type: Object,
      default: null,
    },

    selectedQuestion: {
      type: Object,
    },

    asyncForEach: {
      type: Function,
    },

    showUnanswered: {
      type: Boolean,
      default: false,
    },

    hideList: {
      optional: true,
      type: Boolean,
      default: false,
    },
  },

  mixins: [FirmPreferenceMixins],

  data() {
    return {
      predefinedQuestionAnswers,
      documentsToImport: [],
      documentUploading: false,
      uploadOptions: [
        {
          text: 'Provided Above',
          mobile_text: 'Provided Above',
          value: 'Provided in Questionnaire',
        },
        {
          text: 'Provided Elsewhere (e.g. Email)',
          mobile_text: 'Provided Elsewhere',
          value: 'Provided Elsewhere',
        },
        {
          text: 'Upload Later',
          mobile_text: 'Upload Later',
          value: 'Upload Later',
        },
        {
          text: 'Not Applicable',
          mobile_text: 'Not Applicable',
          value: 'Not Applicable',
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'user',
    }),
    allDocumentsNeeded() {
      if (this.currentQuestionnaire && this.remainingItems) {
        return this.currentQuestionnaire.questionnaire_questions
          .filter(q => q.triggered && q.question_type === 'upload')
          .concat(
            this.remainingItems.questionnaire_questions.filter(
              q => q.triggered && q.question_type === 'upload',
            ),
          );
      } else {
        return this.currentQuestionnaire.questionnaire_questions.filter(
          q => q.triggered && q.question_type === 'upload',
        );
      }
    },
    currentQuestionnaireOtherDocuments() {
      if (
        this.currentQuestionnaire &&
        this.currentQuestionnaire.extracted_documents &&
        this.remainingItems &&
        this.remainingItems.extracted_documents
      ) {
        return this.currentQuestionnaire.extracted_documents
          .filter(d => d.source.toLowerCase().includes(`/bulk uploads/`))
          .concat(
            this.remainingItems.extracted_documents.filter(d =>
              d.source.toLowerCase().includes(`/bulk uploads/`),
            ),
          );
      } else if (
        this.currentQuestionnaire &&
        this.currentQuestionnaire.extracted_documents
      ) {
        return this.currentQuestionnaire.extracted_documents.filter(d =>
          d.source.toLowerCase().includes(`/bulk uploads/`),
        );
      }
    },
    areAllDocumentsNamed() {
      return this.documentsToImport.every(d => !isEmpty(d.name));
    },
  },

  methods: {
    isEmpty,

    moment,

    filteredUploadOptions(question) {
      let filters = [];
      if (question.disable_answer_later) {
        filters.push('Upload Later');
      }
      if (question.disable_not_applicable) {
        filters.push('Not Applicable');
      }
      if (this.disableProvidedElsewhere) {
        filters.push('Provided Elsewhere');
      }
      return this.uploadOptions.filter(a => !filters.includes(a.value));
    },

    uploadLaterDate(question) {
      if (
        question.answer === 'Upload Later' ||
        question.answer === 'Answer Later'
      ) {
        if (question.answer_by) {
          return ` (${question.answer_by})`;
        } else {
          return ` (Not specified when)`;
        }
      } else {
        return '';
      }
    },

    listDocumentsWording(question) {
      return `${question.answer
        .split('&!&')
        .slice(1)
        .map(a => a.split('/')[a.split('/').length - 1])
        .join(', ')}`;
    },

    openRecognizedDocument(question) {
      this.$emit('preview-document', this.recognizedDocument(question));
    },

    recognizedDocument(question) {
      if (this.allDocuments) {
        return this.allDocuments.find(
          q =>
            q.detailed_name &&
            question.options
              .map(o => o.toLowerCase())
              .includes(q.detailed_name.toLowerCase()),
        );
      }
    },

    listDocuments(answer) {
      return answer
        .split('&!&')
        .filter((item, index) => !(index === 0 && item === ''));
    },

    getParentQuestion(question) {
      return this.currentQuestionnaire.questionnaire_questions.find(
        q => q.id === question.follow_up_question_id,
      );
    },

    formatReason(question, parentQuestion) {
      return `${this.currentUser.is_advisor ? 'Client' : 'You'} answered '${
        question.trigger_for_follow_up_question
      }' to<br/>'${parentQuestion.text}'`;
    },

    async deleteExtractedDocument(id) {
      try {
        const response = await API.extractedDocument.deleteExtractedDocument(
          id,
        );
        if (response.data.error) {
          console.log(response.data.error);
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'Successfully deleted.',
          });
          this.currentQuestionnaire.extracted_documents = this.currentQuestionnaire.extracted_documents.filter(
            d => d.id !== id,
          );
        }
      } catch (e) {
        console.log(e);
      }
    },
    pickDocuments() {
      const input = this.$refs.selectedDocuments;
      input.type = 'text';
      input.type = 'file';
      this.$refs.selectedDocuments.click();
    },

    getExtensionOfFilename(filename) {
      return filename.split('.').pop();
    },

    getNameWithoutExtensionOfFilename(filename) {
      let splitName = filename.split('.');
      splitName.pop();
      return splitName.join('.');
    },

    async addFile(e) {
      const documents = e.dataTransfer.files;
      if (documents) {
        const documentType = this.selectedDocumentType;
        this.documentsToImport.push(
          ...Array.from(documents).map(document => {
            return {
              name: this.getNameWithoutExtensionOfFilename(document.name),
              data: document,
              extension: this.getExtensionOfFilename(document.name),
            };
          }),
        );
        this.uploadBulkDocument();
      }
    },

    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    saveQuestion(question, value) {
      question.answer = value;
      this.$emit('save-question', question);
      if (question.answer === 'Not Applicable') {
        this.$emit(
          'open-comment-dialog',
          question,
          'Please explain why this is not applicable or no longer applicable.',
        );
      } else if (question.answer === 'Provided Elsewhere') {
        this.$emit(
          'open-comment-dialog',
          question,
          'Please explain where/how you provided this document (e.g. Email, Mail, In-person).',
        );
      } else if (
        question.answer === 'Upload Later' ||
        question.answer === 'Answer Later'
      ) {
        this.$emit('open-answer-later-dialog', question);
      }
    },

    async onDocumentsPicked(e) {
      const documents = e.target.files;
      if (documents) {
        this.documentsToImport.push(
          ...Array.from(documents).map(document => {
            return {
              name: this.getNameWithoutExtensionOfFilename(document.name),
              data: document,
              extension: this.getExtensionOfFilename(document.name),
            };
          }),
        );
        this.uploadBulkDocument();
      }
    },

    async uploadBulkDocument() {
      this.documentUploading = true;
      let path;
      if (isEmpty(this.currentQuestionnaire.document_path)) {
        path = '';
      } else {
        path = this.currentQuestionnaire.document_path
          .replace('{year}', this.currentQuestionnaire.tax_year)
          .replace('{section}', 'Bulk Uploads')
          .replace('{questionnaire_name}', this.currentQuestionnaire.name);
      }
      try {
        await this.asyncForEach(
          this.documentsToImport,
          async documentToImport => {
            const {
              data: { id, upload_link },
            } = await API.integration.getTemporaryUploadLink({
              companyId: this.currentQuestionnaire.company_id,
            });

            await API.integration.uploadToDropbox(
              upload_link,
              documentToImport.data,
            );

            const response = await API.integration.processDocument({
              id,
              documentName:
                documentToImport.name + '.' + documentToImport.extension,
              companyId: this.currentQuestionnaire.company_id,
              path: '/'.concat(path),
              questionnaireId: this.currentQuestionnaire.id,
            });

            if (response.data.error) {
              console.log(response.data.error);
            } else {
              await this.sleep(2000);
              this.$emit('refresh-questionnaire');
            }
          },
        );
        this.documentsToImport = [];
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem uploading the documents.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.documentUploading = false;
      }
    },
  },
};
</script>

<style lang="postcss" scoped>
.section-title {
  font-weight: 700;
  font-size: 20px;
}
.file-item {
  padding: 10px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  background: #fff;
}
.document-item {
  padding: 5px 0px;
}
.dot {
  color: #999;
  font-size: 18px;
}
.document-item-text {
  font-size: 16px;
}
.select-option {
  & >>> .v-input__control .v-input__slot {
    min-height: 46px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-raidus: 4px;
    font-size: 15px;
  }
}
.why-text {
  font-size: 12px;
}
.action-button {
  & >>> .v-btn__content {
    white-space: normal;
  }
}
.drag-and-drop {
  height: 140px;
  border-radius: 4px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.2);

  & >>> i {
    color: rgba(0, 0, 0, 0.3);
  }

  & >>> span {
    color: rgba(0, 0, 0, 0.6);
  }

  & >>> span.clickable {
    cursor: pointer;
  }
}
.clickable-chip {
  & >>> .v-chip__content {
    cursor: pointer !important;
  }
}
.outlined-red {
  border: 1px solid #cb4039;
}
.drag-and-drop-text {
  font-size: 14px;
}
.max-size-text {
  font-size: 14px;
}
</style>
